import { useEffect, useState, useRef } from 'react';
import { Paper, IconButton, Tooltip, Tabs, Tab, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { useNavigate } from 'react-router';
import { fetchCarePlanNotReviewedMembersChart, exportCarePlanNotReviewedMembersChart } from '../WidgetService';
import MemberPreview from '../../../Models/Member/MemberPreview.model';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import WidgetLoading from '../../SmartDashboard/Shared/WidgetLoading';
import { parentUrl } from '../../../utils/constants';
import { MissingCarePlanSignatureTypeEnum } from '../../../Enum/MIssingCarePlanSignatureTypeEnum';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import { dateFormatter } from '../../../utils/timeFormat';
import { isDefaultDate } from '../../../utils/common';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CarePlanNotReviewedPeriodEnum } from '../../../Enum/CarePlanNotReviewedPeriodEnum';
import b64toBlob from 'b64-to-blob';
import '../Widgets.css';

export const carePlanNotReviewedDateList = [
    {
        Id: CarePlanNotReviewedPeriodEnum.Last90Days,
        Value: CarePlanNotReviewedPeriodEnum.Last90DaysDescription,
    },
    {
        Id: CarePlanNotReviewedPeriodEnum.Last180Days,
        Value: CarePlanNotReviewedPeriodEnum.Last180DaysDescription,
    },
    {
        Id: CarePlanNotReviewedPeriodEnum.Last1Year,
        Value: CarePlanNotReviewedPeriodEnum.Last1YearDescription,
    },
    {
        Id: CarePlanNotReviewedPeriodEnum.Custom,
        Value: CarePlanNotReviewedPeriodEnum.CustomDescription,
    },
];

const CarePlanNotReviewedSummary = (props) => {
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [members, setMembers] = useState<MemberPreview[]>([]);
    const [filteredRows, setFilteredRows] = useState<MemberPreview[]>([]);
    const [show, setShow] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(15);
    const ref = useRef(null);
    const [tab, setTab] = useState(1);
    const {
        carePlanSelectedTeam,
        setCarePlanSelectedTeam,
        carePlanDateFilter,
        setCarePlanDateFilter,
        carePlanDate,
        setCarePlanDate,
        carePlanShowInActiveTeams,
        setCarePlanShowInActiveTeams,
    } = useWidgetContext();

    let dateList = carePlanNotReviewedDateList;

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (carePlanDateFilter !== CarePlanNotReviewedPeriodEnum.Custom || Boolean(carePlanDate)) {
            setMembers([]);
            setIsFetched(false);
            const changeFilter = async () => {
                setIsFetched(false);
                const membersInfo = await fetchCarePlanNotReviewedMembersChart(
                    carePlanSelectedTeam,
                    carePlanDateFilter,
                    carePlanDate ? new Date(carePlanDate).toISOString() : '',
                    carePlanShowInActiveTeams
                );
                setMembers(membersInfo);
                let filterResponse = [];
                switch (tab) {
                    case 1: {
                        filterResponse = membersInfo.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.NotReviewed);
                        break;
                    }
                    case 2: {
                        filterResponse = membersInfo.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.Member);
                        break;
                    }
                    case 3: {
                        filterResponse = membersInfo.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.Provider);
                        break;
                    }
                }
                setFilteredRows(filterResponse);

                setIsFetched(true);
            };
            changeFilter();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carePlanSelectedTeam, carePlanShowInActiveTeams, carePlanDate, carePlanDateFilter]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const columns: GridColDef[] = [
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 140,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 140,
        },
        {
            field: 'Population',
            headerName: 'Population',
            minWidth: 140,
            flex: 1,
        },
        {
            field: 'LastSignedDate',
            headerName: 'Last Date Signed/Updated',
            width: 150,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastSignedDate) ? '-' : dateFormatter(new Date(params.row.LastSignedDate))}</>;
            },
        },
        {
            field: 'UpcomingAppointmentDate',
            headerName: 'Next Upcoming Appointment Date',
            width: 150,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.UpcomingAppointmentDate) ? '-' : dateFormatter(new Date(params.row.UpcomingAppointmentDate))}</>;
            },
        },
        {
            field: 'LastCompletedAppointmentDate',
            headerName: 'Last Completed Appointment Date',
            width: 150,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastCompletedAppointmentDate) ? '-' : dateFormatter(new Date(params.row.LastCompletedAppointmentDate))}</>;
            },
        },
    ];

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        let filterResponse = [];
        switch (newValue) {
            case 1: {
                filterResponse = members.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.NotReviewed);
                break;
            }
            case 2: {
                filterResponse = members.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.Member);
                break;
            }
            case 3: {
                filterResponse = members.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.Provider);
                break;
            }
        }
        setFilteredRows(filterResponse);
        setTab(newValue);
    };

    const navigateToMembers = () => {
        history(`/provider/metricsummary/membermissingsignaturedetail`);
    };

    const exportCarePlanNotReviewedMembers = async () => {
        setDownLoading(true);
        const response = await exportCarePlanNotReviewedMembersChart(
            carePlanSelectedTeam,
            carePlanDateFilter,
            carePlanDate ? new Date(carePlanDate).toISOString() : '',
            carePlanShowInActiveTeams
        );
        let blob = b64toBlob(response.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `CarePlanNotReviewedMembers - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper
                    className="smartdashboard-widget"
                    elevation={5}
                    sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}
                >
                    <Grid container spacing={2}>
                        <Grid size={12} textAlign="center">
                            <Typography color={'primary'} variant="h6" textAlign={'center'} ref={ref}>
                                <span className="pointer" onClick={navigateToMembers}>
                                    Members Missing Care Plan Signature
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="caption">
                                                Care Plans that are missing the member or provider signature, or that have expired signatures.
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        {props.hasElevatedFilterAccess && (
                            <Grid size={3.5}>
                                <TeamDropdown
                                    teams={teams}
                                    selectedTeam={carePlanSelectedTeam}
                                    onChange={(id) => setCarePlanSelectedTeam(id)}
                                    showInactive={carePlanShowInActiveTeams}
                                    onToggleInactive={setCarePlanShowInActiveTeams}
                                />
                            </Grid>
                        )}
                        <Grid size={3}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={carePlanDateFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setCarePlanDateFilter(parseInt(e.target.value as any))}
                                >
                                    {dateList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={3}>
                            {carePlanDateFilter === CarePlanNotReviewedPeriodEnum.Custom ? (
                                <DesktopDatePicker
                                    label="Date"
                                    value={Boolean(carePlanDate) ? carePlanDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => setCarePlanDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            ) : (
                                <></>
                            )}
                        </Grid>
                        <Grid size={2.5}>
                            <Button
                                style={{ minWidth: '120px', marginLeft: 'auto' }}
                                onClick={() => exportCarePlanNotReviewedMembers()}
                                variant="contained"
                                color="success"
                                disabled={downLoading || members?.length === 0}
                            >
                                {downLoading ? 'Exporting...' : 'Export'}
                            </Button>
                        </Grid>
                        <Grid size={12} display={'flex'} mb={1}>
                            <Tabs sx={{ display: 'inline-flex', marginRight: 'auto' }} value={tab} onChange={handleAlertsTabChange}>
                                <Tab value={1} label="Not Reviewed" />
                                <Tab value={2} label="Missing Member Signature" />
                                <Tab value={3} label="Missing Provider Signature" />
                            </Tabs>
                        </Grid>
                        <Grid size={12}>
                            <div style={{ width: '100%', height: 'calc(100vh - 300px)', maxHeight: '420px', position: 'relative' }}>
                                {!isFetched && <WidgetLoading />}
                                <Grid size={12}>
                                    <div style={{ width: '100%', height: 'calc(100vh - 285px)', maxHeight: '400px' }}>
                                        <DataGrid
                                            onCellClick={(params: any) => {
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/integrated-care-plan`, '_blank');
                                            }}
                                            rows={filteredRows}
                                            columns={columns}
                                            paginationModel={{ pageSize: pageSize, page: page }}
                                            onPaginationModelChange={(model) => {
                                                setPageSize(model.pageSize);
                                                setPage(model.page);
                                            }}
                                            pageSizeOptions={[5]}
                                            getRowId={(row) => row.Id}
                                            getRowHeight={() => 'auto'}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                                '& .is-late': {
                                                    color: 'red',
                                                },
                                                '& .is-in-progress': {
                                                    color: 'green',
                                                },
                                            }}
                                        />
                                    </div>
                                </Grid>
                            </div>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default CarePlanNotReviewedSummary;
